import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import ProductCard from '@/components/shop/ProductCard';
import { MagnifyingGlass, FunnelSimple, X } from '@phosphor-icons/react';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const initialCat = searchParams.get('category') || '';

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQ);
  const [category, setCategory] = useState(initialCat);

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 120 };
    if (category) params.category = category;
    if (q) params.q = q;
    api.get('/products', { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q]);

  // Sync URL
  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (category) next.category = category;
    setSearchParams(next, { replace: true });
  }, [q, category, setSearchParams]);

  const heading = useMemo(() => {
    if (category) {
      const c = categories.find((c) => c.slug === category);
      return c ? c.name : 'Shop';
    }
    if (q) return `Results for "${q}"`;
    return 'All Products';
  }, [category, q, categories]);

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex flex-col gap-4 mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">{heading}</h1>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="shop-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products..."
              className="w-full h-11 pl-11 pr-4 rounded-full bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm"
            />
            {q && (
              <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 py-1">
          <button
            data-testid="shop-cat-all"
            onClick={() => setCategory('')}
            className={`shrink-0 px-4 h-9 rounded-full text-sm font-medium border transition-colors ${!category ? 'bg-olive-800 text-cream-100 border-olive-800' : 'bg-white text-stone-700 border-stone-200 hover:border-olive-300'}`}
          >All</button>
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`shop-cat-${c.slug}`}
              onClick={() => setCategory(c.slug)}
              className={`shrink-0 px-4 h-9 rounded-full text-sm font-medium border transition-colors ${category === c.slug ? 'bg-olive-800 text-cream-100 border-olive-800' : 'bg-white text-stone-700 border-stone-200 hover:border-olive-300'}`}
            >{c.name}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] rounded-3xl bg-stone-100 animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-16 text-center">
          <FunnelSimple size={40} className="mx-auto text-stone-300" weight="duotone" />
          <p className="mt-3 text-stone-600">No products match your filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="shop-products-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
